import React from 'react';
import clsx from 'clsx';
import styles from './TransportationPriceSection.module.scss';
import PriceField from 'components/UI/V3/Forms/PriceField/PriceField';
import ValueTypeSelector, { ValueTypeSelectorValue } from 'components/UI/V3/Forms/ValueTypeSelector/ValueTypeSelector';
import { usePermissions } from 'hooks';
import lockStyles from '../../../../UI/priceFieldInteractionLock.module.scss';
import SaveCustomPriceCheckbox from '../../../../UI/SaveCustomPriceCheckbox';
import SavedCustomPriceChoice from '../../../../UI/SavedCustomPriceChoice';
import { useSavedCustomPriceChoice } from '../../../../UI/useSavedCustomPriceChoice';

interface TransportationPriceSectionProps {
    price?: number;
    discount?: ValueTypeSelectorValue;
    maxDiscount?: number;
    saveCustomPrice?: boolean;
    useSavedCustomPrice?: boolean;
    petId?: number;
    serviceId?: number;
    catalogPrice?: number;
    customPriceKind?: 'standard' | 'pickup' | 'dropoff';
    onPriceChange?: (value: number) => void;
    onDiscountChange?: (value: ValueTypeSelectorValue) => void;
    onSaveCustomPriceChange?: (checked: boolean) => void;
    onUseSavedCustomPriceChange?: (useSaved: boolean, price: number, customPriceId?: number) => void;
}

export const TransportationPriceSection: React.FC<TransportationPriceSectionProps> = ({
    price = 0,
    discount,
    maxDiscount,
    saveCustomPrice,
    useSavedCustomPrice,
    petId,
    serviceId,
    catalogPrice,
    customPriceKind,
    onPriceChange,
    onDiscountChange,
    onSaveCustomPriceChange,
    onUseSavedCustomPriceChange
}) => {
    const permissions = usePermissions();
    const canEditPrices = permissions.checkout.editPrices;
    const canApplyDiscount = permissions.checkout.applyDiscounts;
    const hasDiscount = (discount?.value ?? 0) > 0;
    const savedCustomPrice = useSavedCustomPriceChoice({
        petId,
        serviceId,
        catalogPrice: Number(catalogPrice) || 0,
        currentPrice: Number(price) || 0,
        useSavedCustomPrice,
        kind: customPriceKind
    });

    const handlePriceChange = (value?: number) => {
        const nextValue = value ?? 0;
        onPriceChange?.(nextValue);
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <div
                    className={clsx(styles.priceRow, {
                        [lockStyles.interactionLock]: !canEditPrices
                    })}
                >
                    <p className={styles.title}>Price</p>

                    <PriceField
                        className={styles.priceField}
                        value={price}
                        placeholder="0.00"
                        onChange={handlePriceChange}
                    />
                </div>

                <div
                    className={clsx(styles.discountRow, {
                        [lockStyles.interactionLock]: !canApplyDiscount && hasDiscount,
                        [lockStyles.hiddenComponent]: !canApplyDiscount && !hasDiscount
                    })}
                >
                    <p className={styles.title}>Discount</p>

                    <ValueTypeSelector
                        className={styles.discountField}
                        value={discount}
                        placeholder="$0.00"
                        max={maxDiscount ?? price}
                        onChange={onDiscountChange}
                    />
                </div>
            </div>
            {savedCustomPrice.show && (
                <SavedCustomPriceChoice
                    petName={savedCustomPrice.petName}
                    savedPrice={savedCustomPrice.savedPrice}
                    mode={savedCustomPrice.mode}
                    disabled={!canEditPrices}
                    onChange={mode => {
                        const next = savedCustomPrice.applyMode(mode);
                        onPriceChange?.(next.price);
                        onUseSavedCustomPriceChange?.(next.useSavedCustomPrice, next.price, next.customPriceId);
                    }}
                />
            )}
            <SaveCustomPriceCheckbox
                checked={!!saveCustomPrice}
                disabled={!canEditPrices}
                currentPrice={price}
                defaultPrice={Number(catalogPrice) || 0}
                onChange={onSaveCustomPriceChange}
            />
        </div>
    );
};

export default TransportationPriceSection;
